import axios, {
  AxiosProgressEvent,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import {
  Paginated,
  Pagination,
  PaginationQuery,
  convertToUrlParams,
} from "./common";
import { KnowledgeUnitResponse } from "./knowledgeSpaces";
import type { TwinManagementTypes } from "@inctec/smx-ui-client";
import { applicationLanguage } from "@shared/util/languages";

export type TwinBase = { name: string; uuid: string; templateId: string };

export interface TwinQuery {
  type?: string;
  types?: string[];
  uuid?: string;
  uuids?: string[];
  isTemplate?: boolean;
  templateId?: string;
  templateIds?: string[];
  relationsWith?: string[];
  // properties?: string,
  showProperties?: boolean;
  search?: string;
  active?: boolean;
  namespace?: string;
  sortByAttribute?: string;
  sortOrder?: "asc" | "desc";
  name?: string;
  names?: string[];
  properties?: Array<{
    customId?: string;
    // @DEPRECATED: use customId instead
    name?: string;
    // NOTE: type is REQUIRED here by the backend
    type: string;
    scenario?: string;
    values?: any[];
    modifiedAffectedValues?: (string | number)[];
    modified?: {
      date: string;
      dateEnd?: string;
      comparator: string;
    };
  }>;
  propertyRelationsWith?: Array<{
    twinUuid: string;
    propertyTemplateUuid: string;
  }>;
  sortBy?: {
    property?: {
      customId: string;
      type: string;
    };
  };
}

export type LinkPropertyValue = {
  type: "link";
  protocol: "http" | "https" | "mailto" | "sivas";
  path?: string;
  label?: string;
};

export function isLinkPropertyValue(
  value: unknown,
): value is LinkPropertyValue {
  if (typeof value !== "object" || value === null || !("type" in value)) {
    return false;
  }

  return (
    value.type === "link" &&
    "protocol" in value &&
    (value.protocol === "http" ||
      value.protocol === "https" ||
      value.protocol === "mailto" ||
      value.protocol === "sivas")
  );
}

export type TwinPropertyValue =
  | string
  | number
  | LinkPropertyValue
  | {
      uuid: string;
      name: string;
    }
  | string[];

export type TwinProperty = Property & {
  type: {
    id: string;
    type: "timeseries" | "static";
  };
  propertyValue?: {
    modified: string;
    value: TwinPropertyValue;
  };
  propertyTemplateUuid?: string;
  constraint: PropertyConstraint;
};

export type TwinResponse = Omit<
  TwinManagementTypes.TwinResponse,
  "initial" | "properties" | "templateUuid" | "version"
> & {
  templateUuid: string;
  version: number | string;
  initial: string;
  properties?: Array<TwinProperty>;
  writeAccess?: boolean;
  relations?: Array<TwinResponse>;
  // NOTE: in search relationsWith has to be set to retrieve this information
  relationsWith?: Array<{
    uuid: string;
    fromUuid: string;
    toUuid: string;
    contextId: string;
    attributes?: Record<string, any>;
  }>;
};

export type TwinRequest = Omit<
  TwinManagementTypes.CreateTwinData["body"],
  "initial" | "version"
> & {
  version: string | number;
  initial?: string | null;
};

export function searchTwins(
  paging: PaginationQuery,
  request: TwinQuery,
  options?: AxiosRequestConfig,
): AxiosPromise<Paginated<TwinResponse[]>> {
  return axios.post(
    `/api/twins/search?${new URLSearchParams({ page: paging.page?.toString() ?? "1", pageSize: paging.pageSize?.toString() ?? "10" })}`,
    request,
    options,
  );
}

export function createTwin(request: TwinRequest): AxiosPromise<TwinResponse> {
  return axios.post("/api/twins", {
    ...request,
    language: request.language ?? applicationLanguage.value,
  });
}

type TwinCreateWithProperties = TwinRequest &
  Pick<TwinManagementTypes.PostTwinDispatcherData["body"], "knowledge"> & {
    properties: Array<{
    // NOTE: This is the customId
    property: string;
    value: unknown;
  }>;
    knowledgeUnit?: {
      spaces?: string[];
      newVersionOfUnit?: string;
      activateUnit?: boolean;
    };
    comment?: string;
    longComment?: string;
  };

export function createTwinWithProperties(
  request: TwinCreateWithProperties,
): AxiosPromise<TwinResponse & { kUnitUuid: string }> {
  // NOTE: null values for properties are not supported by the backend, thus we filter them out
  const filteredRequest = {
    ...request,
    language: request.language ?? applicationLanguage.value,
    properties: request.properties.filter(
      (pv) => pv.value !== null && pv.value !== undefined,
    ),
  };

  return axios.post("/api/dispatcher/twins", filteredRequest);
}

export function getTwin(
  id: string,
  options?: AxiosRequestConfig,
): AxiosPromise<TwinResponse> {
  return axios.get(`/api/twins/${id}`, options);
}

export type TwinPatch = {
  name?: string;
  description?: string;
  active?: boolean;
  type?: string;
  comment?: string;
  longComment?: string;
};

export function patchTwin(id: string, request: TwinPatch) {
  // NOTE: though this is a patch endpoint, the backend currently requires all data to be set!
  return axios.patch(`/api/twins/${id}`, request);
}

export interface PropertyCreate {
  twinUuid: string;
  constraintUuid?: string;
  name: string;
  title: string;
  description: string;
  unit?: string;
  order?: number;
  customId?: string;
  isTimeSeries: boolean;
  isUserRelevant: boolean;
  type:
    | "float"
    | "text"
    | "json"
    | "floatArray"
    | "twinRelation"
    | "userRelationArray";
  constraint?: PropertyConstraint;
}

export type Property = PropertyCreate & {
  uuid: string;
  modified: string;
  created: string;
  isSystemManaged: boolean;
};

export function getProperties(query: PropertyQuery) {
  const queryParams = new URLSearchParams(query);

  return axios.get(`/api/properties?${queryParams.toString()}`);
}

export function createProperty(
  request: PropertyCreate,
): AxiosPromise<Property> {
  return axios.post(`/api/properties`, request);
}

export function patchProperty(
  id: string,
  request: {
    name?: string;
    title?: string;
    description?: string;
    order?: number;
    customId?: string;
  },
) {
  return axios.patch(`/api/properties/${id}`, request);
}

export function deleteProperty(id: string) {
  return axios.delete(`/api/properties/${id}`);
}

export type PropertyTextOptions = {
  maxLength?: number;
  type?: "text" | "textarea";
  rows?: number;
  llmSearchable?: boolean;
  keywordIndex?: {
    method: "single" | "comma-separated" | "words" | "extracted";
    scope: "property" | "section";
  };
};

export type PropertyConstraintSettings = {
  inputValue?: string;
  inputValueFilterProperty?: string | null;
  multiple?: boolean;
  notUserEditable?: boolean;
  editableOnCreate?: boolean;
  // Used for input values, if true the values can be created on the fly
  allowCreate?: boolean;
  relation?: {
    templateIds: string[];
    types: string[];
  };
  mandatory?: boolean;
  textOptions?: PropertyTextOptions;
  section?: string;
  durationInputs?: (
    | "seconds"
    | "minutes"
    | "hours"
    | "days"
    | "weeks"
    | "months"
    | "years"
  )[];
};

export interface PropertyConstraintRequest {
  name?: string;
  type?: string;
  // @deprecated
  enumValues?: string[];
  settings?: PropertyConstraintSettings;
}

export interface PropertyConstraint extends PropertyConstraintRequest {
  uuid?: string;
  created?: string;
  modified?: string;
}

export function getPropertyConstraints(): AxiosPromise<{
  result: PropertyConstraint[];
}> {
  return axios.get(`/api/properties/constraints`);
}

export function createPropertyConstraints(
  request: PropertyConstraintRequest,
): AxiosPromise<PropertyConstraint> {
  return axios.post(`/api/properties/constraints`, request);
}

export function patchPropertyConstraints(
  id: string,
  request: PropertyConstraintRequest,
) {
  return axios.patch(`/api/properties/constraints/${id}`, request);
}

// TODO: update to new backend functionality

export type PropertyQuery = Partial<
  Record<"twinUuid" | "isTimeSeries", string>
>;

export function getTwins() {
  return axios.get(`/api/twins/`);
}

export function getTwinsByFilter(query: TwinQuery) {
  return axios.get(`/api/twins?${convertToUrlParams(query)}`);
}

// Relations
export type TwinRelationsQuery = {
  page: number;
  pageSize: number;
  contextId?: string;
  // NOTE: could be source or target
  twinUuid?: string;
};

export type TwinRelationRequest = {
  fromUuid: string;
  toUuid: string;
  contextId: string;
  attributes: {
    name?: string;
    role?: string;
    propertyUuid?: string;
    toTemplateUuid?: string;
    fromTemplateUuid?: string;
    propertyTemplateUuid?: string;
    amount?: number;
  };
};

export type TwinRelationResponse = TwinRelationRequest & {
  uuid: string;
  created: string;
  modified: string;
};

export function getTwinRelations(
  query: TwinRelationsQuery,
): AxiosPromise<Paginated<TwinRelationResponse[]> & { twins: TwinResponse[] }> {
  return axios.get(
    `/api/relations?${new URLSearchParams(convertToUrlParams(query))}`,
  );
}

export function postTwinRelation(
  request: TwinRelationRequest,
): AxiosPromise<TwinRelationResponse> {
  return axios.post(`/api/relations`, request);
}

export function deleteTwinRelation(id: string) {
  return axios.delete(`/api/relations/${id}`);
}

export type TwinTemplateRelations = {
  relationsFrom: TwinResponse[];
  relationsTo: TwinResponse[];
  property: Array<{
    template: TwinResponse;
    property: Property;
  }>;
};

export function getTwinTemplateRelations(id: string) {
  return axios.get(
    `/api/template-relations/?${new URLSearchParams({ twinUuid: id })}`,
  );
}

export async function postTwinCsvUpload(
  templateId: string,
  formData: FormData,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
) {
  return axios({
    method: "post",
    url: `/api/dispatcher/twin-batch?${new URLSearchParams({ templateUuid: templateId })}`,
    data: formData,
    headers: {
      "Content-Type": `multipart/form-data;`,
    },
    onUploadProgress,
  });
}
